import { EarningsInsight, EarningsItemWithEntity } from '@api';
import { Icon } from '@design/components/icon/Icon';
import {
  type AppTableFeatures,
  WithNAFallback,
} from '@design/components/table';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { getPriceStatus } from '@helpers';
import { ColumnDef } from '@tanstack/react-table';
import { EntityCell } from '@terminal/components/entity-cell/EntityCell';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { formatNumber } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';
import { getFormattedDates } from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';
import { TFunction, t } from 'i18next';

import sharedStyles from '../../calendar-shared-styles/CalendarSharedStyles.module.scss';
import { EarningInsightsCell } from '../components/earning-insights-cell/EarningInsightsCell';
import tableStyles from '../EventsTable.module.scss';
import { EARNINGS_INSIGHTS_COLUMN_ID, SORT_UNDEFINED } from './constants';

interface EarningsColumnsParams {
  t: TFunction<['calendar']>;
  isBasicOrLightUser: boolean;
  earningsInsightByEarningsId?: Map<string, EarningsInsight>;
  isEarningsInsightsFetching: boolean;
}

export const getEarningsColumns = ({
  isBasicOrLightUser,
  earningsInsightByEarningsId,
  isEarningsInsightsFetching,
}: EarningsColumnsParams): ColumnDef<
  AppTableFeatures,
  EarningsItemWithEntity
>[] => {
  const earningInsights: ColumnDef<AppTableFeatures, EarningsItemWithEntity>[] =
    isBasicOrLightUser
      ? []
      : [
          {
            id: EARNINGS_INSIGHTS_COLUMN_ID,
            header: t('calendar:table.earningInsights'),
            cell: props => (
              <EarningInsightsCell
                earningsInsight={earningsInsightByEarningsId?.get(
                  props.row.original.id
                )}
                entityTag={props.row.original.entity?.tag}
                isFetching={isEarningsInsightsFetching}
                source={POSTHOG_TRACKING_SOURCES.EVENTS}
              />
            ),
            accessorFn: row => row.reporting_date,
            sortFn: 'datetime',
            sortUndefined: SORT_UNDEFINED,
          },
        ];

  return [
    {
      id: 'reporting_date',
      header: t('calendar:table.time'),
      cell: props => {
        const { reporting_date: reportingDate } = props.row.original;
        const date = new Date(reportingDate);
        const { isBeforeNineThirtyAm, isAfterFourPm } = getFormattedDates(date);

        return (
          <WithNAFallback
            value={
              date !== undefined && (
                <div className={tableStyles.timeWrapper}>
                  <Tooltip
                    isTouchDevice={false}
                    trigger={TooltipTrigger.Hover}
                    label={
                      <span>
                        {formatInTimeZone(
                          date,
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                          'h:mmaaa zz',
                          {
                            locale: enUS,
                          }
                        )}
                      </span>
                    }
                  >
                    <div className={tableStyles.timeIconWrapper}>
                      {isBeforeNineThirtyAm && (
                        <Icon
                          iconName="SunRise"
                          size={16}
                          className={sharedStyles.sunRise}
                        />
                      )}
                      {isAfterFourPm && (
                        <Icon
                          iconName="Moon"
                          size={16}
                          className={sharedStyles.moon}
                        />
                      )}
                      {!isBeforeNineThirtyAm && !isAfterFourPm && (
                        <Icon
                          iconName="Sun"
                          size={16}
                          className={sharedStyles.sun}
                        />
                      )}
                    </div>
                  </Tooltip>
                  {format(date, 'HH:mm')}
                </div>
              )
            }
          />
        );
      },
      accessorFn: row => new Date(row.reporting_date),
      sortFn: 'datetime',
    },
    {
      id: 'entity',
      header: t('calendar:table.asset'),
      cell: props => {
        const { entity } = props.row.original;

        return entity?.tag && <EntityCell entityTag={entity.tag} />;
      },
      accessorFn: row =>
        row.entity?.name_short || row.entity?.name_full || row.entity?.name,
      sortUndefined: SORT_UNDEFINED,
    },
    ...earningInsights,
    {
      id: 'eps.estimate',
      header: t('calendar:table.expectedEps'),
      cell: props => {
        const {
          eps: { estimate },
        } = props.row.original;

        return (
          <WithNAFallback
            value={
              estimate !== undefined && (
                <span>
                  {formatNumber({ isPercentage: false, value: estimate })}
                </span>
              )
            }
          />
        );
      },
      accessorFn: row => row.eps.estimate,
      sortUndefined: SORT_UNDEFINED,
    },
    {
      id: 'reported',
      header: t('calendar:table.reported'),
      cell: props => {
        const {
          eps: { value },
        } = props.row.original;

        return (
          <WithNAFallback
            value={
              value !== undefined && (
                <span>{formatNumber({ isPercentage: false, value })}</span>
              )
            }
          />
        );
      },
      accessorFn: row => row.eps.value,
      sortUndefined: SORT_UNDEFINED,
    },
    {
      id: 'revenue.surprise',
      header: t('calendar:table.surpriseAmount'),
      cell: props => {
        const {
          eps: { surprise },
        } = props.row.original;

        return (
          <WithNAFallback
            value={
              surprise !== undefined && (
                <div
                  className={clsx(
                    tableStyles.surpriseWrapper,
                    tableStyles[getPriceStatus(surprise)]
                  )}
                >
                  {formatNumber({ isPercentage: false, value: surprise })}
                </div>
              )
            }
          />
        );
      },
      accessorFn: row => row.eps.surprise,
      sortUndefined: SORT_UNDEFINED,
    },
    {
      id: 'revenue.surprise_percentage',
      header: t('calendar:table.surprisePercentage'),
      cell: props => {
        const {
          eps: { surprise_percentage: surprisePercentage },
        } = props.row.original;

        return (
          <WithNAFallback
            value={
              surprisePercentage !== undefined && (
                <div
                  className={clsx(
                    tableStyles.surpriseWrapper,
                    tableStyles[getPriceStatus(surprisePercentage)]
                  )}
                >
                  {formatNumber({
                    isPercentage: true,
                    value: surprisePercentage,
                    addPlus: true,
                  })}
                </div>
              )
            }
          />
        );
      },
      accessorFn: row => row.eps.surprise_percentage,
      sortUndefined: SORT_UNDEFINED,
    },
  ];
};
